import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SearchIcon, PenToolIcon, RocketIcon, GraduationCapIcon, HeadphonesIcon } from 'lucide-react';
const steps = [{
  number: '01',
  icon: SearchIcon,
  title: 'Discovery & Analyse',
  duration: 'Woche 1',
  description: 'Wir analysieren Ihre aktuellen Vertriebsprozesse, identifizieren Engpässe und definieren klare Ziele. Sie erhalten einen detaillierten Audit-Report mit konkreten Handlungsempfehlungen.',
  deliverables: ['Prozess-Audit', 'Gap-Analyse', 'Zieldefinition', 'Projektplan']
}, {
  number: '02',
  icon: PenToolIcon,
  title: 'Konzeption & Design',
  duration: 'Woche 1-2',
  description: 'Basierend auf der Analyse entwickeln wir Ihr maßgeschneidertes CRM-Konzept. Wir definieren Pipeline-Stufen, Automatisierungen und Integrationen – alles abgestimmt auf Ihre Arbeitsweise.',
  deliverables: ['Pipeline-Design', 'Workflow-Konzept', 'Integrations-Plan', 'Datenmodell']
}, {
  number: '03',
  icon: RocketIcon,
  title: 'Implementierung',
  duration: 'Woche 2-3',
  description: 'Wir richten Close CRM ein, konfigurieren alle Automatisierungen und migrieren Ihre bestehenden Daten. Jede Integration wird getestet und dokumentiert.',
  deliverables: ['CRM-Setup', 'Datenmigration', 'Automatisierungen', 'Integrationen']
}, {
  number: '04',
  icon: GraduationCapIcon,
  title: 'Training & Onboarding',
  duration: 'Woche 3-4',
  description: 'Ihr Team erhält intensives Training – nicht nur zur Bedienung, sondern zu Best Practices im Vertrieb. Wir stellen sicher, dass jeder das System effektiv nutzt.',
  deliverables: ['Team-Schulung', 'Video-Tutorials', 'Dokumentation', 'Best Practices']
}, {
  number: '05',
  icon: HeadphonesIcon,
  title: 'Support & Optimierung',
  duration: 'Fortlaufend',
  description: 'Nach dem Go-Live sind wir weiter für Sie da. Wir überwachen die Nutzung, optimieren Prozesse und helfen bei Fragen. Ihr Erfolg ist unser Erfolg.',
  deliverables: ['Laufender Support', 'Monatliche Reviews', 'Kontinuierliche Optimierung', 'Neue Features']
}];
export function ProcessSteps() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" ref={ref} data-id="element-202">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-204">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-205">
            Unser Prozess
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-206">
            In 5 Schritten zum optimierten Vertrieb
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-207">
            Unser bewährter Prozess garantiert eine reibungslose Implementierung
            – von der ersten Analyse bis zum laufenden Betrieb.
          </p>
        </motion.div>

        <div className="relative" data-id="element-208">
          {/* Connection line */}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#0ea5e9] via-[#0ea5e9]/50 to-transparent hidden md:block" data-id="element-209" />

          <div className="space-y-12" data-id="element-210">
            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.15
          }} className={`relative flex flex-col lg:flex-row items-start gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-211">
                {/* Step number indicator */}
                <div className="absolute left-8 lg:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#0ea5e9] border-4 border-[#0c0c0c] hidden md:block" data-id="element-212" />

                {/* Content card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'}`} data-id="element-213">
                  <div className="p-8 rounded-2xl bg-[#1a1a1a] border border-white/5" data-id="element-214">
                    <div className="flex items-start gap-4 mb-6" data-id="element-215">
                      <div className="w-14 h-14 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0" data-id="element-216">
                        <step.icon className="w-7 h-7 text-[#0ea5e9]" data-id="element-217" />
                      </div>
                      <div data-id="element-218">
                        <div className="flex items-center gap-3 mb-1" data-id="element-219">
                          <span className="text-[#0ea5e9] font-mono font-bold" data-id="element-220">
                            {step.number}
                          </span>
                          <span className="text-xs text-gray-500 px-2 py-1 rounded-full bg-white/5" data-id="element-221">
                            {step.duration}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-white" data-id="element-222">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-400 leading-relaxed mb-6" data-id="element-223">
                      {step.description}
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-224">
                      {step.deliverables.map((deliverable, i) => <span key={i} className="px-3 py-1 text-xs font-medium text-[#0ea5e9] bg-[#0ea5e9]/10 rounded-full" data-id="element-225">
                          {deliverable}
                        </span>)}
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block flex-1" data-id="element-226" />
              </motion.div>)}
          </div>
        </div>

        {/* Timeline summary */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#0ea5e9]/10 to-[#06b6d4]/10 border border-[#0ea5e9]/20 text-center" data-id="element-227">
          <p className="text-xl lg:text-2xl text-white font-medium" data-id="element-228">
            <span className="text-[#0ea5e9]" data-id="element-229">Gesamtdauer:</span> 2-4 Wochen bis
            zum Go-Live –
            <span className="text-white font-bold" data-id="element-230"> 3x schneller</span> als der
            Branchendurchschnitt.
          </p>
        </motion.div>
      </div>
    </section>;
}